import styled, {keyframes} from 'styled-components';

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const fadeInUp = keyframes`
  from { opacity: 0; transform: translate3d(0, 8px, 0); }
  to { opacity: 1; transform: translate3d(0, 0, 0); }
`;

type SkeletonProps = {
  width?: number | string;
  height?: number | string;
  radius?: number;
  circle?: boolean;
  delay?: number;
};

const toCssSize = (value?: number | string) =>
  typeof value === 'number' ? `${value}px` : value;

export const Skeleton = styled.div<SkeletonProps>`
  display: block;
  flex-shrink: 0;
  width: ${(p) => toCssSize(p.width) ?? '100%'};
  height: ${(p) => toCssSize(p.height) ?? '16px'};
  border-radius: ${(p) =>
    p.circle ? '999px' : `${p.radius ?? p.theme.borderRadii.s}px`};
  background-color: rgba(47, 48, 113, 0.07);
  background-image: ${(p) => p.theme.gradients.shimmer};
  background-size: 200% 100%;
  background-repeat: no-repeat;
  animation: ${shimmer} 1.4s ${(p) => p.theme.motion.easing.standard} infinite;
  animation-delay: ${(p) => p.delay ?? 0}ms;
  overflow: hidden;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const TextBlock = styled.div<{$gap: number}>`
  display: flex;
  flex-direction: column;
  gap: ${(p) => p.$gap}px;
  width: 100%;
`;

interface SkeletonTextProps {
  lines?: number;
  lineHeight?: number;
  gap?: number;
  lastLineWidth?: string;
  className?: string;
}

export const SkeletonText = ({
  lines = 3,
  lineHeight = 12,
  gap = 8,
  lastLineWidth = '60%',
  className,
}: SkeletonTextProps) => (
  <TextBlock $gap={gap} className={className}>
    {Array.from({length: lines}).map((_, index) => (
      <Skeleton
        key={index}
        height={lineHeight}
        radius={6}
        delay={index * 90}
        width={index === lines - 1 ? lastLineWidth : '100%'}
      />
    ))}
  </TextBlock>
);

export const SkeletonSurface = styled.div<{$delay?: number}>`
  background: ${(p) => p.theme.gradients.surface};
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: ${(p) => p.theme.borderRadii.m}px;
  box-shadow: ${(p) => p.theme.elevation.sm};
  animation: ${fadeInUp} ${(p) => p.theme.motion.duration.slow}
    ${(p) => p.theme.motion.easing.out} both;
  animation-delay: ${(p) => p.$delay ?? 0}ms;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const CardRow = styled(SkeletonSurface)`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  margin: 12px 0;
`;

interface SkeletonCardProps {
  thumbSize?: number;
  lines?: number;
  round?: boolean;
  delay?: number;
}

export const SkeletonCard = ({
  thumbSize = 72,
  lines = 3,
  round = false,
  delay = 0,
}: SkeletonCardProps) => (
  <CardRow $delay={delay}>
    {thumbSize > 0 ? (
      <Skeleton
        width={thumbSize}
        height={thumbSize}
        radius={round ? undefined : 14}
        circle={round}
        delay={delay}
      />
    ) : null}
    <SkeletonText lines={lines} lastLineWidth="45%" />
  </CardRow>
);

interface SkeletonListProps {
  count?: number;
  thumbSize?: number;
  lines?: number;
  round?: boolean;
}

export const SkeletonList = ({
  count = 6,
  thumbSize = 72,
  lines = 3,
  round = false,
}: SkeletonListProps) => (
  <div aria-busy="true" aria-live="polite">
    {Array.from({length: count}).map((_, index) => (
      <SkeletonCard
        key={index}
        thumbSize={thumbSize}
        lines={lines}
        round={round}
        delay={index * 70}
      />
    ))}
  </div>
);

const VotingListSkeletonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
  width: 100%;
`;

const VotingSkeletonCard = styled(SkeletonSurface)<{$delay: number}>`
  width: 100%;
  max-width: 620px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  animation: ${fadeInUp} ${(p) => p.theme.motion.duration.slow}
    ${(p) => p.theme.motion.easing.out} both;
  animation-delay: ${(p) => p.$delay}ms;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const VotingSkeletonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
`;

const VotingSkeletonMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

export const SkeletonVotingList = ({count = 5}: {count?: number}) => (
  <VotingListSkeletonWrapper aria-busy="true" aria-live="polite">
    {Array.from({length: count}).map((_, index) => (
      <VotingSkeletonCard key={index} $delay={index * 70}>
        <VotingSkeletonRow>
          <Skeleton width={64} height={64} radius={14} delay={index * 70} />
          <VotingSkeletonMeta>
            <Skeleton width="70%" height={15} radius={6} delay={index * 70} />
            <Skeleton width="45%" height={12} radius={6} delay={index * 70 + 40} />
          </VotingSkeletonMeta>
          <Skeleton width={72} height={28} radius={999} delay={index * 70 + 80} />
        </VotingSkeletonRow>
        <Skeleton
          height={46}
          radius={999}
          width="100%"
          delay={index * 70 + 120}
        />
      </VotingSkeletonCard>
    ))}
  </VotingListSkeletonWrapper>
);

const EventListSkeletonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const EventSkeletonCard = styled(SkeletonSurface)<{$delay: number}>`
  width: 100%;
  max-width: 620px;
  min-height: 88px;
  margin-bottom: 12px;
  padding: 8px;
  display: flex;
  align-items: center;
  gap: 12px;
  animation: ${fadeInUp} ${(p) => p.theme.motion.duration.slow}
    ${(p) => p.theme.motion.easing.out} both;
  animation-delay: ${(p) => p.$delay}ms;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const EventSkeletonTexts = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  padding-right: 14px;
`;

const EventSkeletonLeft = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const EventSkeletonRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  flex-shrink: 0;
  width: 38%;
`;

export const SkeletonEventList = ({count = 5}: {count?: number}) => (
  <EventListSkeletonWrapper aria-busy="true" aria-live="polite">
    {Array.from({length: count}).map((_, index) => (
      <EventSkeletonCard key={index} $delay={index * 70}>
        <Skeleton width={64} height={64} radius={10} delay={index * 70} />
        <EventSkeletonTexts>
          <EventSkeletonLeft>
            <Skeleton width="55%" height={12} radius={6} delay={index * 70} />
            <Skeleton width="80%" height={14} radius={6} delay={index * 70 + 40} />
          </EventSkeletonLeft>
          <EventSkeletonRight>
            <Skeleton width={72} height={12} radius={6} delay={index * 70 + 80} />
            <Skeleton width={96} height={10} radius={6} delay={index * 70 + 120} />
          </EventSkeletonRight>
        </EventSkeletonTexts>
      </EventSkeletonCard>
    ))}
  </EventListSkeletonWrapper>
);

const VisibilityListSkeletonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 100%;
  max-width: 630px;
  margin: 0 auto;
`;

const VisibilitySkeletonCard = styled(SkeletonSurface)<{$delay: number}>`
  width: 100%;
  min-height: 72px;
  padding: 16px 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  animation: ${fadeInUp} ${(p) => p.theme.motion.duration.slow}
    ${(p) => p.theme.motion.easing.out} both;
  animation-delay: ${(p) => p.$delay}ms;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const VisibilitySkeletonMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

export const SkeletonVisibilityList = ({count = 5}: {count?: number}) => (
  <VisibilityListSkeletonWrapper aria-busy="true" aria-live="polite">
    {Array.from({length: count}).map((_, index) => (
      <VisibilitySkeletonCard key={index} $delay={index * 70}>
        <VisibilitySkeletonMeta>
          <Skeleton width="55%" height={14} radius={6} delay={index * 70} />
          <Skeleton width="35%" height={10} radius={6} delay={index * 70 + 40} />
        </VisibilitySkeletonMeta>
        <Skeleton
          width={140}
          height={40}
          radius={12}
          delay={index * 70 + 80}
        />
      </VisibilitySkeletonCard>
    ))}
  </VisibilityListSkeletonWrapper>
);

const ClubListSkeletonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const ClubSkeletonCard = styled(SkeletonSurface)<{$delay: number}>`
  width: 100%;
  max-width: 620px;
  margin: 12px 0;
  padding: 12px;
  display: flex;
  align-items: center;
  gap: 14px;
  animation: ${fadeInUp} ${(p) => p.theme.motion.duration.slow}
    ${(p) => p.theme.motion.easing.out} both;
  animation-delay: ${(p) => p.$delay}ms;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const ClubSkeletonMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const ClubSkeletonRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  flex-shrink: 0;
`;

export const SkeletonClubList = ({count = 5}: {count?: number}) => (
  <ClubListSkeletonWrapper aria-busy="true" aria-live="polite">
    {Array.from({length: count}).map((_, index) => (
      <ClubSkeletonCard key={index} $delay={index * 70}>
        <Skeleton width={92} height={92} radius={16} delay={index * 70} />
        <ClubSkeletonMeta>
          <Skeleton width="72%" height={16} radius={6} delay={index * 70} />
          <Skeleton width="58%" height={12} radius={6} delay={index * 70 + 40} />
          <Skeleton width="48%" height={12} radius={6} delay={index * 70 + 80} />
        </ClubSkeletonMeta>
        <ClubSkeletonRight>
          <Skeleton width={84} height={26} radius={999} delay={index * 70 + 120} />
        </ClubSkeletonRight>
      </ClubSkeletonCard>
    ))}
  </ClubListSkeletonWrapper>
);

const ChatListSkeletonWrapper = styled.div`
  width: 100%;
  align-self: stretch;
`;

const ChatListItemRow = styled.div<{$delay: number}>`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px;
  margin: 10px auto;
  width: 100%;
  max-width: 620px;
  box-sizing: border-box;
  border-radius: 20px;
  border: 1px solid ${(p) => p.theme.colors.border};
  background: ${(p) => p.theme.gradients.surface};
  box-shadow: ${(p) => p.theme.elevation.sm};
  animation: ${fadeInUp} ${(p) => p.theme.motion.duration.slow}
    ${(p) => p.theme.motion.easing.out} both;
  animation-delay: ${(p) => p.$delay}ms;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const ChatListItemText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  min-width: 0;
`;

export const SkeletonChatList = ({count = 6}: {count?: number}) => (
  <ChatListSkeletonWrapper aria-busy="true" aria-live="polite">
    {Array.from({length: count}).map((_, index) => (
      <ChatListItemRow key={index} $delay={index * 70}>
        <Skeleton width={64} height={64} radius={12} delay={index * 70} />
        <ChatListItemText>
          <Skeleton width="65%" height={14} radius={6} delay={index * 70} />
          <Skeleton
            width="85%"
            height={12}
            radius={6}
            delay={index * 70 + 40}
          />
        </ChatListItemText>
      </ChatListItemRow>
    ))}
  </ChatListSkeletonWrapper>
);

const GridWrapper = styled.div<{$minWidth: number}>`
  display: grid;
  grid-template-columns: repeat(
    auto-fill,
    minmax(min(100%, ${(p) => p.$minWidth}px), 1fr)
  );
  gap: 16px;
  width: 100%;
`;

const TileBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
`;

interface SkeletonGridProps {
  count?: number;
  minWidth?: number;
  mediaHeight?: number;
}

export const SkeletonGrid = ({
  count = 6,
  minWidth = 260,
  mediaHeight = 140,
}: SkeletonGridProps) => (
  <GridWrapper $minWidth={minWidth} aria-busy="true" aria-live="polite">
    {Array.from({length: count}).map((_, index) => (
      <SkeletonSurface key={index} $delay={index * 70}>
        <Skeleton
          height={mediaHeight}
          radius={0}
          delay={index * 70}
          style={{borderTopLeftRadius: 16, borderTopRightRadius: 16}}
        />
        <TileBody>
          <Skeleton height={14} width="70%" radius={6} delay={index * 70} />
          <SkeletonText lines={2} lineHeight={10} gap={6} />
        </TileBody>
      </SkeletonSurface>
    ))}
  </GridWrapper>
);

const ChatRow = styled.div<{$own: boolean; $delay: number}>`
  display: flex;
  justify-content: ${(p) => (p.$own ? 'flex-end' : 'flex-start')};
  gap: 10px;
  margin: 10px 0;
  animation: ${fadeInUp} ${(p) => p.theme.motion.duration.slow}
    ${(p) => p.theme.motion.easing.out} both;
  animation-delay: ${(p) => p.$delay}ms;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const SkeletonChat = ({count = 6}: {count?: number}) => (
  <div aria-busy="true" aria-live="polite">
    {Array.from({length: count}).map((_, index) => {
      const own = index % 3 === 0;
      return (
        <ChatRow key={index} $own={own} $delay={index * 70}>
          {!own ? <Skeleton width={36} height={36} circle /> : null}
          <Skeleton
            width={`${45 + ((index * 13) % 35)}%`}
            height={index % 2 === 0 ? 56 : 38}
            radius={18}
            delay={index * 70}
          />
        </ChatRow>
      );
    })}
  </div>
);

const FormBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
`;

const FieldBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const SkeletonForm = ({fields = 4}: {fields?: number}) => (
  <FormBlock aria-busy="true" aria-live="polite">
    {Array.from({length: fields}).map((_, index) => (
      <FieldBlock key={index}>
        <Skeleton width="35%" height={10} radius={6} delay={index * 80} />
        <Skeleton height={44} radius={12} delay={index * 80} />
      </FieldBlock>
    ))}
    <Skeleton height={44} radius={999} width="100%" delay={fields * 80} />
  </FormBlock>
);

const DetailsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
`;

const DetailsHero = styled(SkeletonSurface)`
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 20px;
`;

const DetailsHeroText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
  min-width: 0;
`;

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 240px), 1fr));
  gap: 16px;
`;

const DetailsPanel = styled(SkeletonSurface)`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 18px;
`;

export const SkeletonDetails = ({panels = 4}: {panels?: number}) => (
  <DetailsWrapper aria-busy="true" aria-live="polite">
    <DetailsHero $delay={0}>
      <Skeleton width={92} height={92} radius={18} />
      <DetailsHeroText>
        <Skeleton width="55%" height={18} radius={8} />
        <Skeleton width="35%" height={12} radius={6} delay={80} />
        <Skeleton width="25%" height={12} radius={6} delay={160} />
      </DetailsHeroText>
    </DetailsHero>

    <DetailsGrid>
      {Array.from({length: panels}).map((_, index) => (
        <DetailsPanel key={index} $delay={index * 80}>
          <Skeleton width="45%" height={11} radius={6} delay={index * 80} />
          <SkeletonText lines={2} lineHeight={10} gap={8} />
        </DetailsPanel>
      ))}
    </DetailsGrid>
  </DetailsWrapper>
);

const TableWrapper = styled(SkeletonSurface)`
  display: flex;
  flex-direction: column;
  gap: 0;
  padding: 0;
  overflow: hidden;
`;

const TableRow = styled.div<{$header?: boolean}>`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px 18px;
  background: ${(p) =>
    p.$header ? p.theme.colors.surfaceSunken : 'transparent'};
  border-bottom: 1px solid ${(p) => p.theme.colors.border};

  &:last-child {
    border-bottom: none;
  }
`;

export const SkeletonTable = ({
  rows = 6,
  columns = 4,
}: {
  rows?: number;
  columns?: number;
}) => (
  <TableWrapper $delay={0} aria-busy="true" aria-live="polite">
    <TableRow $header>
      {Array.from({length: columns}).map((_, index) => (
        <Skeleton key={index} height={10} radius={6} width="100%" />
      ))}
    </TableRow>
    {Array.from({length: rows}).map((_, rowIndex) => (
      <TableRow key={rowIndex}>
        {Array.from({length: columns}).map((_, colIndex) => (
          <Skeleton
            key={colIndex}
            height={12}
            radius={6}
            width="100%"
            delay={rowIndex * 60 + colIndex * 20}
          />
        ))}
      </TableRow>
    ))}
  </TableWrapper>
);
